import { FC, useCallback, useMemo, useState } from "react";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { FormCategory, TicketingFormCategory } from "@simplyk/common";
import * as QrCode from "qrcode";

import { CloseDialogButton } from "./CloseDialogButton";
import { ItemsQrCodeContent } from "./ItemsQrCodeContent/ItemsQrCodeContent";
import { ShareFormTabLayout } from "./ShareFormTabLayout";
import { ShareFormTabTitle } from "./ShareFormTabTitle";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { useTranslate } from "@/common/hooks/useTranslate";
import { ButtonCopy } from "@/components/ButtonCopy/ButtonCopy";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardInfobox } from "@/dashboard-design-system/Infobox";
import { ChevronLeft } from "@/icons/outlined";

type QrCodeSubTab = "form" | "items";

type QrCodeTabProps = {
  formId: string;
  formLink: string;
  formCategory: FormCategory;
  onClose?: () => void;
  onBackClick?: () => void;
};

export const QrCodeTab: FC<QrCodeTabProps> = ({ formId, formLink, formCategory, onClose, onBackClick }) => {
  const { t } = useTranslate();
  const { logAmplitudeEvent } = useAmplitude();
  const [image, setImage] = useState<{ url: string; blob: Blob } | null>(null);
  const [activeSubTab, setActiveSubTab] = useState<QrCodeSubTab>("form");

  const isAuction = formCategory === TicketingFormCategory.Auction;

  const handleCanvasRef = useCallback(
    (canvas: HTMLCanvasElement | null) => {
      if (!canvas || !formLink) {
        return;
      }
      QrCode.toCanvas(canvas, formLink);
      canvas.toBlob((blob) => {
        if (blob) {
          setImage({ url: URL.createObjectURL(blob), blob });
        }
      });
    },
    [formLink]
  );

  const clipboardItem = useMemo(() => {
    if (image?.blob) {
      return new ClipboardItem({ "image/png": image?.blob });
    }
    return null;
  }, [image?.blob]);

  const handleOnDownload = useCallback(() => {
    logAmplitudeEvent(AmplitudeEvents.ShareFormModalClickSharingQrCode, {
      form_type: formCategory,
      qr_code_sharing_type: "Download image",
    });

    if (image?.url) {
      const a = document.createElement("a");
      a.href = image.url;
      a.download = "qr-code.png";
      a.click();
    }
  }, [formCategory, image?.url, logAmplitudeEvent]);

  const subTabs: Array<{ value: QrCodeSubTab; label: string }> = [
    { value: "form", label: t("common", "shareFormModal.qrCode.tabs.form") },
    { value: "items", label: t("common", "shareFormModal.qrCode.tabs.items") },
  ];

  const formQrContent = (
    <>
      <DashboardInfobox
        vibe="brand"
        type="alert"
        size="medium"
        pointer="bottom"
        title={t("common", "shareFormModal.qrCode.infoBox.title")}
        hideIcon
      >
        {t("common", "shareFormModal.qrCode.infoBox.content")}
      </DashboardInfobox>
      <Stack alignItems="center">
        <canvas ref={handleCanvasRef} width="148px" height="148px" />
        <Stack direction="row" alignItems="center" gap={1}>
          <ButtonCopy
            loading={false}
            vibe="brand"
            size="large"
            variant="outlined"
            toCopy={clipboardItem ? [clipboardItem] : null}
            copyText={t("common", "copyImage")}
            amplitudeEvent={AmplitudeEvents.ShareFormModalClickSharingQrCode}
            amplitudePayload={{ form_type: formCategory, qr_code_sharing_type: "Copy image" }}
          />
          <DashboardButton size="large" vibe="brand" variant="filled" onClick={handleOnDownload}>
            {t("common", "downloadImage")}
          </DashboardButton>
        </Stack>
      </Stack>
    </>
  );

  return (
    <ShareFormTabLayout
      slots={{
        closeButton: <CloseDialogButton onClick={onClose} />,
        header: <ShareFormTabTitle>{t("common", "shareFormModal.qrCode.title")}</ShareFormTabTitle>,
        content: isAuction ? (
          <Stack gap={2} alignItems="center">
            <Stack direction="row" gap={1} role="tablist">
              {subTabs.map(({ value, label }) => {
                const isActive = activeSubTab === value;
                return (
                  <Box
                    key={value}
                    component="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => setActiveSubTab(value)}
                    sx={(theme) => ({
                      px: 2,
                      py: 1,
                      borderRadius: 1,
                      border: "none",
                      cursor: "pointer",
                      transition: "background-color 150ms ease-out, color 150ms ease-out",
                      ...theme.typography.subtitle2,
                      color: isActive ? theme.palette.text.brand.intense : theme.palette.text.brand.moderate,
                      backgroundColor: isActive ? theme.palette.surface.brand.moderate : "transparent",
                      "&:hover": {
                        backgroundColor: isActive
                          ? theme.palette.surface.brand.moderate
                          : theme.palette.surface.brand.quiet,
                      },
                    })}
                  >
                    {label}
                  </Box>
                );
              })}
            </Stack>
            <Stack sx={{ width: "100%" }}>
              {activeSubTab === "form" && formQrContent}
              {activeSubTab === "items" && (
                <ItemsQrCodeContent formId={formId} formLink={formLink} formCategory={formCategory} />
              )}
            </Stack>
          </Stack>
        ) : (
          formQrContent
        ),
        footer: (
          <Stack direction="row" alignItems="center" justifyContent="flex-start">
            <DashboardButton
              vibe="neutral"
              variant="outlined"
              size="large"
              startIcon={<ChevronLeft />}
              onClick={onBackClick}
            >
              {t("common", "back")}
            </DashboardButton>
          </Stack>
        ),
      }}
    />
  );
};
